/**
 * Token Manager - Caches access tokens to reduce unnecessary refresh calls
 *
 * Mirrors the website's tokenManager.ts implementation but adapted for
 * react-native-app-auth instead of MSAL.
 *
 * Pattern:
 * 1. Cache valid tokens in memory
 * 2. Only refresh when token is near expiry
 * 3. Handle errors gracefully
 */

import { authService } from './authService';
import { Logger } from '../utils/logger';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface CachedToken {
  token: string;
  expiryTimestamp: number; // Unix timestamp in milliseconds
}

// In-memory token cache
let cachedToken: CachedToken | null = null;

// 25 minutes - refresh when 5 minutes remain before actual expiry
const CACHE_DURATION_MS = 25 * 60 * 1000;
const TOKEN_STORAGE_KEY = 'auth_api_token_cache';

/**
 * Get a valid access token, using cache if available
 *
 * @returns Access token string, or null if unavailable
 */
export async function getAccessToken(): Promise<string | null> {
  const now = Date.now();

  // If cached token is still valid, return it
  if (cachedToken && cachedToken.expiryTimestamp > now) {
    Logger.debug(
      'TokenManager',
      `✓ Using cached token (expires in ${Math.round((cachedToken.expiryTimestamp - now) / 1000)}s)`
    );
    return cachedToken.token;
  }

  // Cache miss or expired - get new token from authService
  try {
    Logger.info('TokenManager', '⏳ Acquiring new access token from authService...');
    const token = await authService.getAccessToken();

    if (!token) {
      Logger.warn('TokenManager', 'No token returned from authService');
      cachedToken = null;
      return null;
    }

    // Cache the token
    cachedToken = {
      token,
      expiryTimestamp: now + CACHE_DURATION_MS,
    };

    Logger.info(
      'TokenManager',
      `✓ Token cached (will refresh in ${Math.round(CACHE_DURATION_MS / 1000)}s)`
    );

    return token;
  } catch (error) {
    Logger.error('TokenManager', 'Failed to acquire access token', error);
    cachedToken = null;
    throw error;
  }
}

/**
 * Get access token specifically for API calls (with API scope)
 * Uses the same caching logic as getAccessToken()
 *
 * @returns Access token string with API scope, or null if unavailable
 */
export async function getApiAccessToken(): Promise<string | null> {
  const now = Date.now();

  // Check in-memory cache first
  if (cachedToken && cachedToken.expiryTimestamp > now) {
    Logger.debug(
      'TokenManager',
      `✓ Using cached API token (expires in ${Math.round((cachedToken.expiryTimestamp - now) / 1000)}s)`
    );
    return cachedToken.token;
  }

  // Check persistent storage
  try {
    const stored = await AsyncStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const tokenData = JSON.parse(stored) as CachedToken;
      if (tokenData.expiryTimestamp > now) {
        Logger.debug('TokenManager', '✓ Using token from AsyncStorage');
        cachedToken = tokenData;
        return tokenData.token;
      }
    }
  } catch (error) {
    Logger.warn('TokenManager', 'Failed to read token from storage', error);
  }

  // Cache miss or expired - get new token from authService with API scope
  try {
    Logger.info('TokenManager', '⏳ Acquiring new API access token from authService...');
    const token = await authService.getApiAccessToken();

    if (!token) {
      Logger.warn('TokenManager', 'No API token returned from authService');
      cachedToken = null;
      await AsyncStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }

    // Cache the token in memory AND storage
    cachedToken = {
      token,
      expiryTimestamp: now + CACHE_DURATION_MS,
    };

    // Persist to storage
    try {
      await AsyncStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(cachedToken));
    } catch (storageError) {
      Logger.warn('TokenManager', 'Failed to persist token to storage', storageError);
    }

    Logger.info(
      'TokenManager',
      `✓ API token cached (will refresh in ${Math.round(CACHE_DURATION_MS / 1000)}s)`
    );

    return token;
  } catch (error) {
    Logger.error('TokenManager', 'Failed to acquire API access token', error);
    // Don't throw - return cached token if available, otherwise null
    if (cachedToken) {
      Logger.warn('TokenManager', 'Refresh failed but cached token still available');
      return cachedToken.token;
    }
    cachedToken = null;
    await AsyncStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
}

/**
 * Clear the token cache (e.g., on logout)
 */
export async function clearTokenCache(): Promise<void> {
  cachedToken = null;
  try {
    await AsyncStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    Logger.warn('TokenManager', 'Failed to clear token from storage', error);
  }
  Logger.info('TokenManager', 'Token cache cleared');
}

/**
 * Get cache status for debugging
 */
export function getTokenCacheStatus() {
  if (!cachedToken) {
    return { cached: false, message: 'No cached token' };
  }

  const now = Date.now();
  const remaining = cachedToken.expiryTimestamp - now;

  if (remaining > 0) {
    return {
      cached: true,
      valid: true,
      expiresInSeconds: Math.round(remaining / 1000),
      message: `Valid, expires in ${Math.round(remaining / 1000)}s`,
    };
  }

  return {
    cached: true,
    valid: false,
    expiredSeconds: Math.round(-remaining / 1000),
    message: `Expired ${Math.round(-remaining / 1000)}s ago`,
  };
}
